import { useEffect, useRef, useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { getUserCredential } from "src/lib/cookies";
import { getCompStatementResult } from "../services";

/*
 * Custom hook to fetch and manage Compensation Statement Result data.
 * Handles API calls based on providerId and periodTypeId changes.
 * Only calls the API once initially, then on period type changes.
 */
export const useCompStatementResult = (providerId, periodTypeId) => {
  const dispatch = useDispatch();
  const cookiesData = useMemo(() => getUserCredential(), []);

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const requestRef = useRef(0);

  useEffect(() => {
    // Skip if required parameters are missing
    if (!providerId || !periodTypeId) {
      setData([]);
      setIsLoading(false);
      return;
    }

    const token = requestRef.current + 1;
    requestRef.current = token;
    setIsLoading(true);
    setError(null);

    (async () => {
      try {
        const result = await getCompStatementResult(cookiesData, dispatch, providerId, periodTypeId);

        // Only update if this is still the latest request
        if (requestRef.current === token) {
          setData(result);
          setIsLoading(false);
        }
      } catch (err) {
        if (requestRef.current === token) {
          setError(err);
          setData([]);
          setIsLoading(false);
        }
      }
    })();

    return () => {
      // Supersede any in-flight request
      requestRef.current += 1;
    };
  }, [providerId, periodTypeId, cookiesData, dispatch]);

  return {
    data,
    isLoading,
    error,
  };
};
